"use client";

/**
 * Compact source chips displayed beneath an AI chat message.
 *
 * Extracted from `compass-intakeflow.jsx:188-196` (`.if-chatsrcs`, `.if-srcchip`,
 * `.if-scn`, `.if-scl` classes).
 *
 * Shows a wrapping row of numbered citation chips — each with a provenance
 * indicator and a label — that the user can click to open the citation detail panel.
 */

import type { ProvenanceType } from "../lib/provenance";
import { PROV_META } from "../lib/provenance";

/* ---------- types ---------- */

export interface ChatSrcChip {
  /** Citation number shown on the chip (matches inline superscript). */
  n: number;
  /** Human-readable citation label. */
  label: string;
  /** Provenance type for the indicator box. */
  type: ProvenanceType;
}

export interface ChatSrcChipsProps {
  /** Chips to display; nothing is rendered when the array is empty/undefined. */
  chips?: ChatSrcChip[];
  /** Called with the citation number when a chip is clicked. */
  onOpen?: (n: number) => void;
}

/* ---------- provenance box background map ----------
 * Maps to `cs-pd.sm` in the design (22×22px solid-colored rounded square).
 * Matches `.cs-pd.provided`, `.cs-pd.estimated`, `.cs-pd.catalog` CSS rules.
 */
const PROV_BOX_CLS: Record<string, string> = {
  provided: "bg-[var(--prov)]",
  estimated: "bg-[var(--est)]",
  vault: "bg-[var(--accent-plum)]",
  catalog: "bg-[var(--accent-plum)]",
  empty: "bg-[var(--empty)]",
};

/**
 * Wrapping row of compact citation chips placed beneath an AI message bubble.
 *
 * `.if-chatsrcs` → flex wrap, gap-1.5
 * `.if-srcchip`  → inline-flex, white bg, border, rounded-sm, padded, hover highlight
 * `.if-scn`      → citation number: shrink-0, navy, bold, 10.5px
 * `.cs-pd.sm`    → 22×22px solid-colored rounded square with white icon
 * `.if-scl`      → label: truncated at 180px
 */
export function ChatSrcChips({ chips, onOpen }: ChatSrcChipsProps) {
  if (!chips || chips.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5">
      {chips.map((chip) => {
        // Normalize "vault" → "catalog" for the metadata lookup
        const key = chip.type === "vault" ? "catalog" : chip.type;
        const meta = PROV_META[key] ?? PROV_META.estimated;
        const IconComponent = meta.icon;

        return (
          <button
            key={chip.n}
            type="button"
            className={[
              "inline-flex items-center gap-1.5 max-w-full",
              "font-[family-name:var(--font-body)] text-[11.5px] font-medium",
              "text-(--fg-secondary)",
              "bg-(--bg-white) border border-(--border-default)",
              "rounded-(--radius-sm)",
              "py-1 pr-[9px] pl-[7px]",
              "cursor-pointer",
              "transition-all duration-300",
              "hover:border-(--border-strong) hover:bg-(--bg-sky) hover:text-(--fg-primary)",
            ].join(" ")}
            onClick={() => onOpen?.(chip.n)}
          >
            {/* Citation number */}
            <span className="shrink-0 text-[10.5px] font-bold text-(--fg-brand)">
              {chip.n}
            </span>
            {/* Provenance square indicator — cs-pd sm */}
            <span
              className={[
                "inline-flex items-center justify-center",
                "h-[22px] w-[22px]",
                "rounded-(--radius-xs)",
                "shrink-0",
                "text-white",
                PROV_BOX_CLS[chip.type] ?? PROV_BOX_CLS.estimated,
              ].join(" ")}
              title={meta.label}
              role="img"
              aria-label={`Provenance: ${meta.label}`}
            >
              <IconComponent size={11} />
            </span>
            {/* Label */}
            <span className="whitespace-nowrap overflow-hidden text-ellipsis max-w-[180px]">
              {chip.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
